import axios, { InternalAxiosRequestConfig } from "axios";

const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

// Store for MSAL instance reference (set by main app)
let msalInstanceRef: any = null;

// Function to set MSAL instance from the app
export const setMsalInstance = (instance: any) => {
  msalInstanceRef = instance;
};

// Automatically attach access token to all requests
axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      config.headers = config.headers || {};

      // Handle Content-Type for FormData vs JSON
      if (config.data instanceof FormData) {
        // Remove Content-Type to let browser set multipart boundary
        if (config.headers && "Content-Type" in config.headers) {
          delete config.headers["Content-Type"];
        }
      } else {
        // Default to JSON for non-FormData requests
        if (!config.headers["Content-Type"]) {
          config.headers["Content-Type"] = "application/json";
        }
      }

      let token: string | null = null;

      // Try to get fresh token from MSAL if available
      if (msalInstanceRef) {
        try {
          const accounts = msalInstanceRef.getAllAccounts();
          if (accounts.length > 0) {
            const account = accounts[0];
            const scopes = [import.meta.env.VITE_API_SCOPE];

            const response = await msalInstanceRef.acquireTokenSilent({
              account,
              scopes,
            });

            if (response?.accessToken) {
              token = response.accessToken;
              // Update localStorage for consistency
              try {
                localStorage.setItem("access_token", response.accessToken);
              } catch (e) {
                console.warn("Could not update localStorage:", e);
              }
            }
          }
        } catch (msalError) {
          console.error("❌ MSAL token acquisition failed, clearing storage and forcing re-login:", msalError);
          // Clear token from localStorage to force fresh login
          localStorage.removeItem("access_token");
          // Redirect to logout
          if (msalInstanceRef) {
            msalInstanceRef.logoutRedirect({ postLogoutRedirectUri: "/" });
          }
        }
      }

      // Fallback to localStorage if MSAL didn't provide token
      if (!token) {
        token = localStorage.getItem("access_token");
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log(
          `✅ Token attached to ${config.method?.toUpperCase()} ${config.url}`
        );
      } else {
        console.warn(
          `⚠️ No token found for ${config.method?.toUpperCase()} ${config.url}`
        );
      }
    } catch (error) {
      console.error("Error in request interceptor:", error);
    }

    return config;
  }
);

export default axiosClient;
